/**
 * @engine/core - Level
 * 
 * Container for GameObjects. Manages object lifecycle,
 * hierarchy, and provides lookup by ID/GUID/name.
 * 
 * Note: Renamed from Scene to Level to avoid confusion with
 * rendering/visual scenes and match Unity's newer terminology.
 */

import { GameObject, resetIdCounters } from './GameObject';
import { Transform } from './Transform';
import { SceneEvents } from './SceneEvents';
import { generateKeyBetween } from 'fractional-indexing';
import type {
  LevelId,  // Keep internal type name for backward compat
  GameObjectId,
  GUID,
  Tag,
  GameObjectConfig,
} from './types';
import { generateGUID } from './types';

let nextLevelId = 1;

/**
 * Reset level ID counter (for testing)
 */
export function resetLevelIdCounter(): void {
  nextLevelId = 1;
}

/**
 * Level configuration for creation/deserialization
 */
export interface LevelConfig {
  name?: string;
  guid?: GUID;
}

/**
 * Editor folder for organizing hierarchy (editor-only, not a GameObject)
 */
export interface EditorFolder {
  /** Unique folder ID */
  id: GUID;
  /** Display name */
  name: string;
  /** Parent folder ID (null = root level) */
  parentId: GUID | null;
  /** Custom icon name (lucide icon name) */
  icon?: string;
  /** Custom color (hex string, e.g. "#ff5500") */
  color?: string;
  /** Notes/comments for this folder */
  notes?: string;
  /** Sort order key (fractional indexing) */
  order: string;
  /** Child GameObject GUIDs (ordering) */
  childObjects: GUID[];
  /** Child folder IDs (ordering) */
  childFolders: GUID[];
}

/**
 * Level state for serialization
 */
export interface LevelState {
  id: LevelId;
  guid: GUID;
  name: string;
  rootObjects: GUID[];
}

/**
 * Level - Container for GameObjects
 * 
 * Manages:
 * - GameObject creation/destruction
 * - Lookup by ID, GUID, name, tag
 * - Hierarchy (root objects)
 * - Serialization/deserialization
 * 
 * @example
 * ```typescript
 * const level = new Level({ name: 'MainLevel' });
 * const player = level.createGameObject('Player');
 * const enemy = level.createGameObject('Enemy');
 * 
 * // Find objects
 * const found = level.findByName('Player');
 * const byTag = level.findAllByTag('Enemy');
 * 
 * // Iterate
 * level.forEach(go => console.log(go.name));
 * ```
 */
export class Level {
  private _id: LevelId;
  private _guid: GUID;
  private _name: string;
  
  // Storage
  private _objectsById: Map<GameObjectId, GameObject> = new Map();
  private _objectsByGuid: Map<GUID, GameObject> = new Map();
  private _rootObjects: Set<GameObjectId> = new Set();
  
  // Editor folders (keyed by folder ID)
  private _folders: Map<GUID, EditorFolder> = new Map();
  
  // Fractional indexing order keys for root-level items
  private _rootObjectOrders: Map<GUID, string> = new Map();
  
  // Lifecycle tracking
  private _pendingDestroy: Set<GameObjectId> = new Set();

  // Scene-level event bus
  private _events = new SceneEvents();
  
  constructor(config: LevelConfig = {}) {
    this._id = nextLevelId++;
    this._guid = config.guid ?? generateGUID();
    this._name = config.name ?? 'Level';
  }
  
  // ==========================================================================
  // IDENTITY
  // ==========================================================================
  
  /** Runtime ID */
  get id(): LevelId {
    return this._id;
  }
  
  /** Persistent GUID */
  get guid(): GUID {
    return this._guid;
  }
  
  /** Display name */
  get name(): string {
    return this._name;
  }
  
  set name(value: string) {
    this._name = value;
  }
  
  /** Scene event bus — subscribe for object/component lifecycle notifications */
  get events(): SceneEvents {
    return this._events;
  }

  // ==========================================================================
  // GAMEOBJECT CREATION
  // ==========================================================================
  
  /**
   * Create a new GameObject in this level
   */
  createGameObject(name?: string): GameObject {
    return this.createGameObjectWithConfig(name !== undefined ? { name } : {});
  }
  
  /**
   * Create a new GameObject with full config
   */
  createGameObjectWithConfig(config: GameObjectConfig): GameObject {
    const go = new GameObject(config);
    this.addGameObject(go);
    return go;
  }
  
  /**
   * Add existing GameObject to level
   */
  addGameObject(go: GameObject): void {
    // Remove from previous level
    if (go.sceneId !== null && go.sceneId !== this._id) {
      // TODO: LevelManager.getLevel(go.sceneId)?.removeGameObject(go);
    }
    
    go._setScene(this._id);
    this._objectsById.set(go.id, go);
    this._objectsByGuid.set(go.guid, go);
    
    // Wire component lifecycle callbacks → queue events
    go._onComponentAdded = (g, typeId, compId) => this._events.enqueueComponentAdded(g, typeId, compId);
    go._onComponentRemoved = (g, typeId, compId) => this._events.enqueueComponentRemoved(g, typeId, compId);
    
    // If no parent, it's a root object
    if (go.transform.parent === null) {
      this._rootObjects.add(go.id);
      if (!this._rootObjectOrders.has(go.guid)) {
        // Generate order key at end
        this._rootObjectOrders.set(go.guid, this._generateRootObjectOrder());
      }
    }

    // Queue event (dispatched at start of next update by SceneEventsSubsystem)
    this._events.enqueueObjectAdded(go);
  }
  
  /**
   * Remove GameObject from level (doesn't destroy it)
   */
  removeGameObject(go: GameObject): void {
    // Queue removal event (dispatched at start of next update by SceneEventsSubsystem)
    this._events.enqueueObjectRemoved(go);

    // Detach component callbacks
    go._onComponentAdded = null;
    go._onComponentRemoved = null;

    this._objectsById.delete(go.id);
    this._objectsByGuid.delete(go.guid);
    this._rootObjects.delete(go.id);
    this._rootObjectOrders.delete(go.guid);
    go._setScene(null);
  }

  /**
   * Update root status of a GameObject based on current parent.
   * Call this after changing transform.parent to keep _rootObjects in sync.
   */
  updateRootStatus(go: GameObject): void {
    if (go.transform.parent === null) {
      this._rootObjects.add(go.id);
      if (!this._rootObjectOrders.has(go.guid)) {
        this._rootObjectOrders.set(go.guid, this._generateRootObjectOrder());
      }
    } else {
      this._rootObjects.delete(go.id);
      this._rootObjectOrders.delete(go.guid);
    }
  }
  
  /**
   * Generate a new order key at the end of root objects
   */
  private _generateRootObjectOrder(): string {
    const orders = [...this._rootObjectOrders.values()].sort();
    const lastOrder = orders.length > 0 ? orders[orders.length - 1] : null;
    return generateKeyBetween(lastOrder, null);
  }
  
  /**
   * Mark GameObject for destruction (processed at end of frame)
   */
  destroyGameObject(go: GameObject): void {
    this._pendingDestroy.add(go.id);
  }
  
  /**
   * Destroy GameObject immediately
   */
  destroyGameObjectImmediate(go: GameObject): void {
    // Destroy children first (reverse order for safety)
    const transform = go.transform;
    for (let i = transform.childCount - 1; i >= 0; i--) {
      const childTransform = transform.getChild(i);
      if (childTransform) {
        const childGo = this.findByTransform(childTransform);
        if (childGo) {
          this.destroyGameObjectImmediate(childGo);
        }
      }
    }
    
    // Remove from parent
    if (transform.parent) {
      transform.parent = null;
    }
    
    // Remove components
    // TODO: Notify ComponentRegistry to cleanup
    
    // Remove from level (emits objectRemoved + detaches callbacks)
    this.removeGameObject(go);
  }
  
  // ==========================================================================
  // LOOKUP
  // ==========================================================================
  
  /**
   * Find GameObject by runtime ID
   */
  findById(id: GameObjectId): GameObject | null {
    return this._objectsById.get(id) ?? null;
  }
  
  /**
   * Find GameObject by GUID
   */
  findByGuid(guid: GUID): GameObject | null {
    return this._objectsByGuid.get(guid) ?? null;
  }
  
  /**
   * Find first GameObject with name
   */
  findByName(name: string): GameObject | null {
    for (const go of this._objectsById.values()) {
      if (go.name === name) return go;
    }
    return null;
  }
  
  /**
   * Find all GameObjects with name
   */
  findAllByName(name: string): GameObject[] {
    const result: GameObject[] = [];
    for (const go of this._objectsById.values()) {
      if (go.name === name) result.push(go);
    }
    return result;
  }
  
  /**
   * Find first GameObject with tag
   */
  findByTag(tag: Tag): GameObject | null {
    for (const go of this._objectsById.values()) {
      if (go.tag === tag) return go;
    }
    return null;
  }
  
  /**
   * Find all GameObjects with tag
   */
  findAllByTag(tag: Tag): GameObject[] {
    const result: GameObject[] = [];
    for (const go of this._objectsById.values()) {
      if (go.tag === tag) result.push(go);
    }
    return result;
  }
  
  /**
   * Find GameObject by Transform
   */
  findByTransform(transform: Transform): GameObject | null {
    return this.findById(transform.gameObjectId);
  }
  
  // ==========================================================================
  // ITERATION
  // ==========================================================================
  
  /**
   * Get all root GameObjects (no parent) in display order
   */
  getRootObjects(): GameObject[] {
    const result: GameObject[] = [];
    for (const id of this._rootObjects) {
      const go = this._objectsById.get(id);
      if (go) result.push(go);
    }
    // Sort by order key
    result.sort((a, b) => {
      const orderA = this._rootObjectOrders.get(a.guid) ?? 'a0';
      const orderB = this._rootObjectOrders.get(b.guid) ?? 'a0';
      return orderA.localeCompare(orderB);
    });
    return result;
  }
  
  /**
   * Get all GameObjects in level
   */
  getAllObjects(): GameObject[] {
    return Array.from(this._objectsById.values());
  }
  
  /**
   * Total number of GameObjects
   */
  get objectCount(): number {
    return this._objectsById.size;
  }
  
  /**
   * Iterate all GameObjects
   */
  forEach(callback: (go: GameObject) => void): void {
    for (const go of this._objectsById.values()) {
      callback(go);
    }
  }
  
  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================
  
  /**
   * Process pending destruction (called at end of frame)
   */
  processPendingDestroy(): void {
    for (const id of this._pendingDestroy) {
      const go = this._objectsById.get(id);
      if (go) {
        this.destroyGameObjectImmediate(go);
      }
    }
    this._pendingDestroy.clear();
  }
  
  /**
   * Update internal state tracking (e.g., root objects after hierarchy change)
   */
  updateHierarchy(): void {
    this._rootObjects.clear();
    
    for (const go of this._objectsById.values()) {
      if (go.transform.parent === null) {
        this._rootObjects.add(go.id);
      }
    }
  }
  
  // ==========================================================================
  // SERIALIZATION
  // ==========================================================================
  
  /**
   * Get level state for serialization
   */
  toState(): LevelState {
    const rootGuids: GUID[] = [];
    for (const id of this._rootObjects) {
      const go = this._objectsById.get(id);
      if (go) rootGuids.push(go.guid);
    }
    
    return {
      id: this._id,
      guid: this._guid,
      name: this._name,
      rootObjects: rootGuids,
    };
  }
  
  // ==========================================================================
  // EDITOR FOLDERS
  // ==========================================================================
  
  /**
   * Create a new editor folder
   */
  createFolder(name: string, parentId: GUID | null = null): EditorFolder {
    // Generate order key for new folder
    const order = this._generateFolderOrder(parentId);
    
    const folder: EditorFolder = {
      id: generateGUID(),
      name,
      parentId,
      order,
      childObjects: [],
      childFolders: [],
    };
    this._folders.set(folder.id, folder);
    
    // Add to parent's childFolders if has parent
    if (parentId) {
      const parent = this._folders.get(parentId);
      if (parent) {
        parent.childFolders.push(folder.id);
      }
    }
    
    return folder;
  }
  
  /**
   * Generate order key for a new folder (at end of siblings)
   */
  private _generateFolderOrder(parentId: GUID | null): string {
    let siblings: EditorFolder[];
    if (parentId) {
      const parent = this._folders.get(parentId);
      siblings = parent ? parent.childFolders
        .map(id => this._folders.get(id))
        .filter((f): f is EditorFolder => f !== undefined) : [];
    } else {
      siblings = this.getRootFolders();
    }
    siblings.sort((a, b) => a.order.localeCompare(b.order));
    const lastOrder = siblings.length > 0 ? siblings[siblings.length - 1].order : null;
    return generateKeyBetween(lastOrder, null);
  }
  
  /**
   * Get folder by ID
   */
  getFolder(id: GUID): EditorFolder | undefined {
    return this._folders.get(id);
  }
  
  /**
   * Get all folders
   */
  getAllFolders(): Map<GUID, EditorFolder> {
    return this._folders;
  }
  
  /**
   * Get root folders (no parent) in display order
   */
  getRootFolders(): EditorFolder[] {
    const result: EditorFolder[] = [];
    for (const folder of this._folders.values()) {
      if (folder.parentId === null) {
        result.push(folder);
      }
    }
    // Sort by order key
    result.sort((a, b) => a.order.localeCompare(b.order));
    return result;
  }
  
  /**
   * Update folder properties
   */
  updateFolder(id: GUID, updates: Partial<Omit<EditorFolder, 'id'>>): void {
    const folder = this._folders.get(id);
    if (folder) {
      Object.assign(folder, updates);
    }
  }
  
  /**
   * Delete a folder (moves children to parent or root)
   */
  deleteFolder(id: GUID): void {
    const folder = this._folders.get(id);
    if (!folder) return;
    
    // Move child objects to parent folder or root
    if (folder.parentId) {
      const parent = this._folders.get(folder.parentId);
      if (parent) {
        parent.childObjects.push(...folder.childObjects);
        parent.childFolders.push(...folder.childFolders);
        // Remove this folder from parent
        const idx = parent.childFolders.indexOf(id);
        if (idx !== -1) parent.childFolders.splice(idx, 1);
      }
    }
    
    // Update child folders' parentId
    for (const childFolderId of folder.childFolders) {
      const childFolder = this._folders.get(childFolderId);
      if (childFolder) {
        childFolder.parentId = folder.parentId;
      }
    }
    
    this._folders.delete(id);
  }
  
  /**
   * Add a GameObject to a folder
   */
  addObjectToFolder(folderId: GUID, objectGuid: GUID): void {
    const folder = this._folders.get(folderId);
    if (folder && !folder.childObjects.includes(objectGuid)) {
      folder.childObjects.push(objectGuid);
    }
  }
  
  /**
   * Remove a GameObject from a folder
   */
  removeObjectFromFolder(folderId: GUID, objectGuid: GUID): void {
    const folder = this._folders.get(folderId);
    if (folder) {
      const idx = folder.childObjects.indexOf(objectGuid);
      if (idx !== -1) folder.childObjects.splice(idx, 1);
    }
  }
  
  /**
   * Find which folder contains a GameObject (if any)
   */
  findFolderContaining(objectGuid: GUID): EditorFolder | undefined {
    for (const folder of this._folders.values()) {
      if (folder.childObjects.includes(objectGuid)) {
        return folder;
      }
    }
    return undefined;
  }
  
  /**
   * Move a folder to a new parent
   */
  moveFolder(folderId: GUID, newParentId: GUID | null): void {
    const folder = this._folders.get(folderId);
    if (!folder) return;
    
    // Prevent moving folder into itself or its descendants
    if (newParentId) {
      let current: GUID | null = newParentId;
      while (current) {
        if (current === folderId) return; // Would create cycle
        const parent = this._folders.get(current);
        current = parent?.parentId ?? null;
      }
    }
    
    // Remove from old parent
    if (folder.parentId) {
      const oldParent = this._folders.get(folder.parentId);
      if (oldParent) {
        const idx = oldParent.childFolders.indexOf(folderId);
        if (idx !== -1) oldParent.childFolders.splice(idx, 1);
      }
    }
    
    // Add to new parent
    folder.parentId = newParentId;
    // Generate new order key for end of new parent's children
    folder.order = this._generateFolderOrder(newParentId);
    if (newParentId) {
      const newParent = this._folders.get(newParentId);
      if (newParent && !newParent.childFolders.includes(folderId)) {
        newParent.childFolders.push(folderId);
      }
    }
  }
  
  /**
   * Get order for any item (folder or object) regardless of nesting
   */
  private _getItemOrder(itemId: GUID | null): string | null {
    if (!itemId) return null;
    // Check if it's a folder (any level)
    const folder = this._folders.get(itemId);
    if (folder) return folder.order;
    // Check if it's an object with order
    return this._rootObjectOrders.get(itemId) ?? null;
  }
  
  /**
   * Safe wrapper around generateKeyBetween that handles inconsistent order keys
   */
  private _safeGenerateKeyBetween(before: string | null, after: string | null): string {
    try {
      // If both are provided and before >= after, ignore the constraint
      if (before !== null && after !== null && before.localeCompare(after) >= 0) {
        // Generate key after the "before" item
        return generateKeyBetween(before, null);
      }
      return generateKeyBetween(before, after);
    } catch {
      // Fallback: generate a new key at the end
      return generateKeyBetween(before ?? after, null);
    }
  }
  
  /**
   * Reorder a folder to a specific position (between before and after)
   */
  reorderFolder(folderId: GUID, beforeId: GUID | null, afterId: GUID | null): void {
    const folder = this._folders.get(folderId);
    if (!folder) return;
    
    // Get order from any item (folder or object)
    const beforeOrder = this._getItemOrder(beforeId);
    const afterOrder = this._getItemOrder(afterId);
    
    folder.order = this._safeGenerateKeyBetween(beforeOrder, afterOrder);
  }
  
  /**
   * Reorder a root object to a specific position
   */
  reorderRootObject(objectGuid: GUID, beforeGuid: GUID | null, afterGuid: GUID | null): void {
    // Validate the object exists
    if (!this._objectsByGuid.has(objectGuid)) return;
    
    // Get order from any item (folder or object)
    const beforeOrder = this._getItemOrder(beforeGuid);
    const afterOrder = this._getItemOrder(afterGuid);
    
    this._rootObjectOrders.set(objectGuid, this._safeGenerateKeyBetween(beforeOrder, afterOrder));
  }
  
  /**
   * Get order key for a root object
   */
  getRootObjectOrder(guid: GUID): string | undefined {
    return this._rootObjectOrders.get(guid);
  }
  
  /**
   * Set order key for a root object (for deserialization)
   */
  setRootObjectOrder(guid: GUID, order: string): void {
    this._rootObjectOrders.set(guid, order);
  }
  
  /**
   * Get all root object orders (for serialization)
   */
  getRootObjectOrders(): Map<GUID, string> {
    return this._rootObjectOrders;
  }
  
  /**
   * Set all folders (for deserialization)
   */
  setAllFolders(folders: Map<GUID, EditorFolder>): void {
    this._folders = folders;
  }
  
  /**
   * Clear level (remove all objects).
   *
   * Fires `objectRemoved` for every object and flushes immediately, so subscribed subsystems
   * (Script/Physics/Audio/VFX) run their per-object teardown (destroy bodies/voices/sprite
   * handles) BEFORE the maps are dropped.
   *
   * @param resetIds Reset the global GameObject/Transform/Component ID counters. Default `true`
   *   for full (Single-mode / last-level) teardown. Pass `false` for additive teardown — the
   *   global counters are shared and resetting them would corrupt other still-loaded levels.
   */
  clear(resetIds: boolean = true): void {
    // Notify subscribers so per-object teardown runs while the objects still exist.
    for (const go of this._objectsById.values()) {
      this._events.enqueueObjectRemoved(go);
    }
    this._events.flush();

    this._objectsById.clear();
    this._objectsByGuid.clear();
    this._rootObjects.clear();
    this._rootObjectOrders.clear();
    this._pendingDestroy.clear();
    this._folders.clear();

    // Global ID-counter reset is additive-hostile (shared across all loaded levels).
    if (resetIds) {
      resetIdCounters();
    }
  }
}